import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Copy, Loader2, Printer, Trash2 } from "lucide-react";
import { TechTab } from "@/components/modelsheets/TechTab";
import { costsPayload } from "@/components/modelsheets/StylistTab";
import { labNotes, toPayload } from "@/lib/modelSheets";
import { seesMoney, useAuth } from "@/context/AuthContext";

const withDefaults = (s) => ({
  ...s, notes: labNotes(s), cut: { quantities: {}, notes: "", ...s.cut },
  tech: { measures: [], ...s.tech, accessories: s.tech?.accessories?.length ? s.tech.accessories : [{ name: "", quantity: "" }], instructions: "", fabrics: s.tech?.fabrics?.length ? s.tech.fabrics : s.cut?.fabrics || [] },
});

export const SheetEditor = ({ sheet, seasons, onClose, onSaved }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const money = seesMoney(user);
  const [s, setS] = useState(() => withDefaults(sheet));
  const snap = (x, costs) => JSON.stringify([x, costs || null]);
  const [saved, setSaved] = useState(() => { const w = withDefaults(sheet); return snap(toPayload(w), w.costs); });
  const { data: opts = {} } = useQuery({ queryKey: ["model-sheet-options"], queryFn: async () => (await api.get("/model-sheets/options")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["model-sheets"] });
  const save = useMutation({
    mutationFn: async (body) => {
      const d = (await (s.id ? api.put(`/model-sheets/${s.id}`, body) : api.post("/model-sheets", body))).data;
      return money && s.costs ? (await api.put(`/model-sheets/${d.id}/costs`, costsPayload(s.costs))).data : d;
    },
    onSuccess: (d, body) => { refresh(); setSaved(snap(body, s.costs)); setS((p) => ({ ...p, id: d.id, updated_at: d.updated_at })); onSaved(d); },
    onError: (e) => toast.error(e.response?.data?.detail || "Salvataggio non riuscito"),
  });
  const ensureSaved = async () => (s.id ? s : save.mutateAsync(toPayload(s)));
  const dup = useMutation({ mutationFn: async () => (await api.post(`/model-sheets/${s.id}/duplicate`)).data, onSuccess: (d) => { refresh(); toast.success("Copia creata"); setS(withDefaults(d)); onSaved(d); } });
  const del = useMutation({ mutationFn: async () => api.delete(`/model-sheets/${s.id}`), onSuccess: () => { refresh(); toast.success("Scheda eliminata"); onClose(); } });
  const submit = () => {
    if (!s.name.trim()) { toast.error("Scrivi il nome del modello"); return; }
    save.mutate(toPayload(s), { onSuccess: () => toast.success("Scheda salvata") });
  };
  const print = async (tipo) => {
    if (!s.name.trim()) { toast.error("Scrivi il nome del modello"); return; }
    const w = window.open("about:blank", "_blank");
    try {
      const saved = await save.mutateAsync(toPayload(s));
      if (w) w.location.href = `/modellistica/${saved.id}/stampa?tipo=${tipo}`;
    } catch { w?.close(); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto [&>*]:min-w-0" data-testid="sheet-editor" dirty={snap(toPayload(s), s.costs) !== saved}
        onSave={async () => { if (!s.name.trim()) { toast.error("Scrivi il nome del modello"); throw new Error("nome"); } await save.mutateAsync(toPayload(s)); toast.success("Scheda salvata"); }}>
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2">{s.code && <span className="rounded-md bg-primary px-2.5 py-0.5 font-mono-erp text-base font-bold tracking-wider text-primary-foreground" data-testid="sheet-editor-code">{s.code.toUpperCase()}</span>}{s.id ? s.name || "Scheda tecnica" : "Nuova scheda tecnica"} <span className="text-sm font-normal text-muted-foreground">{s.season}</span></DialogTitle></DialogHeader>
        <Tabs defaultValue="tecnica">
          <TabsList className="max-w-full justify-start overflow-x-auto">
            <TabsTrigger value="tecnica" data-testid="sheet-tab-tecnica">Scheda tecnica</TabsTrigger>
          </TabsList>
          <TabsContent value="tecnica"><TechTab s={s} set={setS} opts={opts} seasons={seasons} ensureSaved={ensureSaved} onImage={(d) => { setS((p) => ({ ...p, id: d.id, image_path: d.image_path, image_type: d.image_type, image_version: d.image_version })); refresh(); }} /></TabsContent>
        </Tabs>
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex gap-2">
            {s.id && <Button variant="ghost" className="text-rose-400" onClick={() => window.confirm(`Eliminare la scheda "${s.name}"?`) && del.mutate()} data-testid="sheet-delete-btn"><Trash2 className="mr-1 h-4 w-4" />Elimina</Button>}
            {s.id && <Button variant="outline" onClick={() => dup.mutate()} data-testid="sheet-duplicate-btn"><Copy className="mr-1 h-4 w-4" />Duplica</Button>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => print("tecnica")} data-testid="sheet-editor-print-tecnica"><Printer className="mr-1 h-4 w-4" />Stampa tecnica</Button>
            <Button onClick={submit} disabled={save.isPending} data-testid="sheet-save-btn">{save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
